import React from "react";
import { REDACTED_VALUE_TEXT } from "../../../../../../constants";
import { prettyPrintJSONLike } from "../../../../../../services";
import { CodeBlockFileDownloadInfo } from "../types";
import { CollapsableSection } from "./LogSidePanelBody";
import { CodeSnippet, Text } from "@merge-api/merge-javascript-shared";
import EmptyStateWrapper from "../../../../../shared/EmptyStateWrapper";

type Props = {
  title: string;
  data: string | undefined; // Query Params, headers, and body all string/json data
  isBody?: boolean; // Only true for Request Body
  isRequest: boolean; // true if this a request body otherwise it's a response body
  downloadInfo?: CodeBlockFileDownloadInfo;
  isCollapsedByDefault?: boolean;
  isRedacted?: boolean;
  isRoundedBottom?: boolean;
};

export const LogSidePanelBodySection = ({
  title,
  data,
  isBody = false,
  isRequest,
  downloadInfo,
  isCollapsedByDefault = false,
  isRedacted,
  isRoundedBottom,
}: Props) => {
  let displayText = data ? prettyPrintJSONLike(data) : "Empty";

  // For large response bodies, truncate to 21 lines
  if (downloadInfo !== undefined) {
    displayText = displayText.split("\n", 21).join("\n");
    displayText += "\n...";
  }

  return (
    <CollapsableSection
      title={title}
      isCollapsedByDefault={isCollapsedByDefault}
      numberOfElements={!isBody && data ? Object.entries(data).length : undefined}
      isRoundedBottom={isRoundedBottom}
      children={
        // Request/response headers, query params, and bodies contain customer
        // payloads — masked from PostHog autocapture and the Frigade Assistant
        // DOM snapshot.
        <div className="w-100 ph-no-capture flex flex-col gap-y-6" data-fr-ignore>
          {data === undefined && !isRedacted ? (
            <EmptyStateWrapper isSpinner />
          ) : isBody || data === undefined ? (
            <CodeSnippet
              title={isRequest ? "Request body" : "Response body"}
              language="json"
              variant={isRequest ? "gray" : "white"}
              showCopyButton={downloadInfo === undefined}
              textToCopy={data}
              footerButton={
                downloadInfo && {
                  label: downloadInfo.previewMessage,
                  href: downloadInfo.downloadLink,
                }
              }
            >
              {isRedacted && (!data || data == "null") ? `Body is redacted.` : displayText}
            </CodeSnippet>
          ) : (
            Object.entries(data).map(([key, value]) => {
              const isRedacted = value === REDACTED_VALUE_TEXT;
              return (
                <div key={key} className="break-all">
                  <Text variant="h6" className="text-gray-40">
                    {key}
                  </Text>
                  <Text className={isRedacted ? "italic" : ""}>{value}</Text>
                </div>
              );
            })
          )}
        </div>
      }
    />
  );
};
